import { forEach, isEmpty, map } from 'lodash';
import {
  deliveryMapper,
  railInterchangeMapper,
  railMapper,
  truckMapper,
  vesselMapper,
} from 'pages/VehicleDetails/sectionMappingVehicleInquiry/transportation';

const generateId = (title) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
};

const getTransportationSectionsByDepartureLocation = ({
  transportation,
  vehicle = null,
  vehicleType,
  isPrint = false,
  searchContextValues = {},
}) => {
  const events = transportation?.Transportation?.events;
  if (!events?.length) {
    return {};
  }

  const isExport = vehicleType === 'export';
  let isFirstVesselLeg = true;
  const locationGroups = {};

  forEach(events, (item) => {
    const location = item?.departure?.departureLocation;
    if (!location) {
      return;
    }

    if (!locationGroups[location]) {
      locationGroups[location] = [];
    }

    if (item?.departure?.name === 'Truck Depart' && item?.departure?.type === 'Logistics') {
      locationGroups[location].push(
        truckMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (item?.departure?.name === 'Rail Depart' && item?.departure?.type === 'Logistics') {
      locationGroups[location].push({
        ...railMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          skipDetailsInPrint: item?.skipDetailsInPrint,
          isCopyCarrierAndRailcar: isEmpty(item?.details),
          isPrint,
          searchContextValues,
        }),
        intersectionDetails: map(item?.details, (intersectionDetail) =>
          railMapper({
            departureData: intersectionDetail?.departure,
            arrivalData: intersectionDetail?.arrival,
            isPrint,
            searchContextValues,
          }),
        ),
      });
    }
    if (
      item?.departure?.name === 'On Truck for Delivery' &&
      item?.departure?.type === 'Logistics'
    ) {
      locationGroups[location].push(
        deliveryMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (
      item?.departure?.name === 'Rail Interchange Depart' &&
      item?.departure?.type === 'Logistics'
    ) {
      locationGroups[location].push(
        railInterchangeMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          skipDetailsInPrint: item?.skipDetailsInPrint,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (item?.departure?.name === 'Vessel Depart' && item?.departure?.type === 'Logistics') {
      locationGroups[location].push(
        vesselMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isExport,
          isFirstVesselLeg,
          isPrint,
          searchContextValues,
        }),
      );
      isFirstVesselLeg = false;
    }
  });

  return locationGroups;
};

const useTransportationSectionItems = ({
  urn,
  modelYear,
  transportationData,
  vehicle = null,
  vehicleType,
  isPrint = false,
}) => {
  if (!urn || !modelYear || !transportationData) {
    return { dynamicItems: [], dynamicSectionData: {} };
  }

  const events = transportationData?.Transportation?.events || [];
  if (!events.length) {
    return { dynamicItems: [], dynamicSectionData: {} };
  }

  const departureLocations = [
    ...new Set(events.map((event) => event?.departure?.departureLocation).filter(Boolean)),
  ];

  if (!departureLocations.length) {
    return { dynamicItems: [], dynamicSectionData: {} };
  }

  const sectionsByLocation = getTransportationSectionsByDepartureLocation({
    transportation: transportationData,
    vehicle,
    vehicleType,
    isPrint,
  });

  const dynamicItems = departureLocations.map((location) => {
    const id = generateId(location);

    return {
      id,
      title: location,
      target: `#${id}`,
      isDynamic: true,
      isLogistics: true,
    };
  });

  const dynamicSectionData = dynamicItems.reduce((acc, item) => {
    acc[item.id] = {
      title: item.title,
      sectionsData: sectionsByLocation[item.title] || [],
    };
    return acc;
  }, {});

  return { dynamicItems, dynamicSectionData };
};

export default useTransportationSectionItems;
